import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, AlertIcon, Box, Flex, Text, Button, Input, InputGroup, InputLeftElement, Avatar, HStack, VStack, Table,
  Thead, Tbody, Tr, Th, Td, IconButton, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay,
  FormControl, FormErrorMessage, Grid, Select, useDisclosure, useColorModeValue, Center, Menu, MenuButton, MenuList, MenuItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Textarea, Spinner, Tooltip,
} from "@chakra-ui/react";

import { AddIcon, SearchIcon, EditIcon, ChevronDownIcon, DownloadIcon, ViewIcon, EmailIcon } from "@chakra-ui/icons";
import Card from "components/card/Card";
import {
  serviceCallRepository,
  Service,
  ServiceCall,
  ServiceCallUpdate,
} from "api/serviceCallRepository";
import { customerRepository, CustomerIdNameAddressPlant } from "api/customer";
import { getMasterDataValues, settingRepository } from "api/setting";
import axiosInstance from "api/axiosinstance";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

interface CustomerWisePagination {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

function useCustomerWiseServiceCalls(
  onLoadError: (error: unknown) => void
) {
  const [serviceCall, setServiceCall] = useState<Service[]>([]);
  const [customerWisePagination, setCustomerWisePagination] =
    useState<CustomerWisePagination>({
      totalCount: 0,
      pageNumber: 1,
      pageSize: 10,
      totalPages: 1,
    });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const requestIdRef = useRef(0);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  const loadservices = useCallback(
    async (
      requestedPage = 1,
      requestedPageSize = 10,
      requestedSearch = ""
    ) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setPageIndex(requestedPage - 1);
      setPageSize(requestedPageSize);

      try {
        const response = await serviceCallRepository.getservicecall(
          requestedPage,
          requestedPageSize,
          requestedSearch.trim()
        );

        if (requestId !== requestIdRef.current) return;

        const totalCount = response.totalCount || 0;
        const currentPage = response.pageNumber || requestedPage;
        const currentPageSize = response.pageSize || requestedPageSize;

        setServiceCall(response.items || []);
        setCustomerWisePagination({
          totalCount,
          pageNumber: currentPage,
          pageSize: currentPageSize,
          totalPages: Math.max(
            1,
            response.totalPages ||
              Math.ceil(totalCount / requestedPageSize)
          ),
        });
        setPageIndex(currentPage - 1);
        setPageSize(currentPageSize);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;

        console.error("Unable to load customer-wise service calls:", error);
        setServiceCall([]);
        setCustomerWisePagination({
          totalCount: 0,
          pageNumber: 1,
          pageSize: requestedPageSize,
          totalPages: 1,
        });
        setPageIndex(0);
        onLoadErrorRef.current(error);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  const handlePageChange = useCallback(
    (newPageIndex: number) => {
      if (
        newPageIndex < 0 ||
        newPageIndex >= customerWisePagination.totalPages
      ) {
        return;
      }
      void loadservices(newPageIndex + 1, pageSize, search);
    },
    [customerWisePagination.totalPages, loadservices, pageSize, search]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      void loadservices(1, newPageSize, search);
    },
    [loadservices, search]
  );

  useEffect(() => {
    void loadservices(1, 10, "");
  }, [loadservices]);

  useEffect(() => {
    const value = searchInput.trim();
    if (value === search) return;

    const timer = window.setTimeout(() => {
      setSearch(value);
      void loadservices(1, pageSize, value);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [loadservices, pageSize, search, searchInput]);

  return {
    serviceCall,
    customerWisePagination,
    search,
    searchInput,
    setSearchInput,
    loading,
    pageSize,
    pageIndex,
    loadservices,
    handlePageChange,
    handlePageSizeChange,
  };
}

const PURPLE = "#7062B9";
const PURPLE_DARK = "#5B4FA3";
const PURPLE_SOFT = "#EEEBFA";
const hasGeneratedReport = (reportGenerated?: boolean | string) =>
  reportGenerated === true ||
  (typeof reportGenerated === "string" &&
    reportGenerated.trim().toLowerCase() === "true");
const NAVY = "#2D2A5E";
const MUTED = "#9A98B8";

const HEADER_GRADIENT = `linear-gradient(90deg, ${PURPLE_DARK}, ${PURPLE})`;

// Solid purple primary button
const primaryBtn = {
  bg: PURPLE,
  color: "white",
  _hover: { bg: PURPLE_DARK },
  _active: { bg: PURPLE_DARK },
};

// Outlined purple button
const outlineBtn = {
  variant: "outline",
  color: PURPLE,
  borderColor: PURPLE,
  _hover: { bg: PURPLE_SOFT },
};

type ServiceCallForm = {
  customerId: string;
  customerName: string;
  location: string;

  machineTypes: string[];
  softwares: string[];
  visitFor: number;
  servicesRendered: string[];
  partsReplaced: string[];

  engineerRemarks: string[];

  customerRemark: string;

  customerEngineer: string;
  customerEngineerDesignation: string;

  cSignature: string;

  plant: string;
  status: string;
};

type SignatureType = "customer";

const CUSTOM_SERVICE = "__CUSTOM_SERVICE__";
const CUSTOM_PART = "__CUSTOM_PART__";
const CUSTOM_REMARK = "__CUSTOM_REMARK__";

const emptyForm = (): ServiceCallForm => ({
  customerId: "",
  customerName: "",
  location: "",

  machineTypes: [],
  softwares: [],

  visitFor: 0,

  servicesRendered: [],
  partsReplaced: [],

  engineerRemarks: [],

  customerRemark: "",

  customerEngineer: "",
  customerEngineerDesignation: "",

  cSignature: "",

  plant: "",
  status: "Open",
});

const dataUrlToFile = (dataUrl: string, fileName: string): File | null => {
  if (!dataUrl || !dataUrl.startsWith("data:")) {
    return null;
  }

  try {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);

    if (!mimeMatch) {
      return null;
    }

    const mime = mimeMatch[1];
    const binaryString = atob(arr[1]);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new File([bytes], fileName, { type: mime });
  } catch (error) {
    console.error("Unable to convert signature:", error);
    return null;
  }
};

const hasSignatureValue = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed === "null" || trimmed === "undefined") return false;
  return true;
};

const formatArrayFiltered = (value: any): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (item) =>
      item &&
      item !== CUSTOM_SERVICE &&
      item !== CUSTOM_PART &&
      item !== CUSTOM_REMARK
  );
};

const ArrayCellList: React.FC<{ items: any }> = ({ items }) => {
  const list = formatArrayFiltered(items);

  if (!list.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        -
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={1}>
      {list.map((item, index) => (
        <Text
          key={`${item}-${index}`}
          fontSize="sm"
          lineHeight="1.3"
          whiteSpace="normal"
          wordBreak="break-word"
        >
          <b>{index + 1}.</b> {item}
        </Text>
      ))}
    </VStack>
  );
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="sm" fontWeight="bold" mb="8px">
    {children}
  </Text>
);

const RemovableList: React.FC<{
  items: string[];
  onRemove: (index: number) => void;
  borderColor: string;
}> = ({ items, onRemove, borderColor }) => {
  if (!items.length) return null;

  return (
    <Box mt={3} border="1px solid" borderColor={borderColor} borderRadius="12px" p={3}>
      {items.map((item, index) => (
        <Flex key={`${item}-${index}`} justify="space-between" align="center" py={1}>
          <Text fontSize="sm">
            <b>{index + 1}.</b> {item}
          </Text>

          <Button size="xs" colorScheme="red" variant="ghost" onClick={() => onRemove(index)}>
            Remove
          </Button>
        </Flex>
      ))}
    </Box>
  );
};

// Signature thumbnail box

const SignaturePreviewBox: React.FC<{
  src: string | null;
  loading: boolean;
  hasValue: boolean;
  alt: string;
  borderColor: string;
}> = ({ src, loading, hasValue, alt, borderColor }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="10px"
      bg="white"
      width="220px"
      height="80px"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      {loading ? (
        <HStack spacing={2} color="gray.400">
          <Spinner size="sm" color={PURPLE} />
          <Text fontSize="xs" color="gray.500">
            Loading...
          </Text>
        </HStack>
      ) : src && !failed ? (
        <img
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          {hasValue ? "Preview unavailable" : "No signature yet"}
        </Text>
      )}
    </Box>
  );
};

export default function ServiceCallReportTable() {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();

  /* ---------- Theme tokens ---------- */

  const tableBg = useColorModeValue("gray.50", "#1B254B");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const headerBg = PURPLE; // used by the inner table in the View modal
  const hoverBg = useColorModeValue(PURPLE_SOFT, "whiteAlpha.100");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const inputBg = useColorModeValue("white", "navy.700");
  const inputBorderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const btnColor = useColorModeValue(NAVY, "white");

  const pageBg = useColorModeValue("#F0F2FF", "#0B1437");
  const cardBg = useColorModeValue("white", "#111C44");
  const rowBg = useColorModeValue("white", "#1B254B");
  const rowAltBg = useColorModeValue("#FAF9FF", "#18224A");
  const pageText = useColorModeValue(NAVY, "white");
  const mutedColor = useColorModeValue(MUTED, "whiteAlpha.600");
  const rowShadow = useColorModeValue(
    "0 1px 3px rgba(112,98,185,0.10)",
    "0 1px 3px rgba(0,0,0,0.4)"
  );

  // shared props for all form fields inside the drawer
  const fieldProps = {
    bg: inputBg,
    color: textColor,
    borderColor: inputBorderColor,
  };

  // plain, muted header cells
  const thStyle = {
    color: mutedColor,
    fontWeight: 500,
    fontSize: "12px",
    textTransform: "none" as const,
    letterSpacing: "0.02em",
    borderBottom: "none",
    py: 2,
  };

  /* ---------- State ---------- */

  const [customers, setCustomers] = useState<CustomerIdNameAddressPlant[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [previewingReportId, setPreviewingReportId] = useState("");
  const [isSendingReportEmail, setIsSendingReportEmail] = useState(false);
  const [reportPreviewUrl, setReportPreviewUrl] = useState<string | null>(null);
  const [previewedReportId, setPreviewedReportId] = useState("");
  const reportPreviewUrlRef = useRef<string | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const centerAlertTimerRef = useRef<number | null>(null);
  const [signatureType, setSignatureType] = useState<SignatureType | null>(null);
  const [isSignatureOpen, setIsSignatureOpen] = useState(false);
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const [form, setForm] = useState<ServiceCallForm>(emptyForm());
  const [cSignaturePreview, setCSignaturePreview] = useState<string | null>(null);
  const [cSignatureLoading, setCSignatureLoading] = useState(false);

  const signatureBlobUrlsRef = useRef<string[]>([]);

  const previewVersionRef = useRef<Record<SignatureType, number>>({ customer: 0 });

  const revokeSignatureBlobs = () => {
    signatureBlobUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_e) {
        // ignore
      }
    });
    signatureBlobUrlsRef.current = [];
  };

  useEffect(() => {
    return () => {
      revokeSignatureBlobs();
    };
  }, []);

  const resolveSignaturePath = (
    sigValue: string | null | undefined
  ): { isDataUrl: boolean; path: string } | null => {
    if (!hasSignatureValue(sigValue)) {
      return null;
    }

    const v = (sigValue as string).trim();

    if (v.startsWith("data:")) {
      return { isDataUrl: true, path: v };
    }

    if (v.startsWith("http://") || v.startsWith("https://")) {
      return { isDataUrl: false, path: v };
    }

    const normalized = v.replace(/\\/g, "/").replace(/^\/+/, "");

    const fileUrl = new URL(normalized, axiosInstance.defaults.baseURL).toString();

    return {
      isDataUrl: false,
      path: `/api/servicecalls/download?fileUrl=${encodeURIComponent(fileUrl)}`,
    };
  };

  const loadSignaturePreview = async (
    sigValue: string | null | undefined,
    type: SignatureType,
    setPreview: (v: string | null) => void,
    setLoading: (v: boolean) => void
  ) => {
    // Claim a version. Anything that bumps the counter later (e.g. saving a new
    // signature) makes this call's result stale and it will be ignored.
    const myVersion = ++previewVersionRef.current[type];
    const isStale = () => previewVersionRef.current[type] !== myVersion;

    const resolved = resolveSignaturePath(sigValue);

    if (!resolved) {
      setPreview(null);
      setLoading(false);
      return;
    }

    if (resolved.isDataUrl || resolved.path.startsWith("http")) {
      setPreview(resolved.path);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.get(resolved.path, {
        responseType: "blob",
      });

      if (isStale()) return;

      const blobUrl = URL.createObjectURL(response.data);

      signatureBlobUrlsRef.current.push(blobUrl);

      setPreview(blobUrl);
    } catch (error: any) {
      console.error("[signature] fetch FAILED", {
        path: resolved.path,
        status: error?.response?.status,
        message: error?.message,
      });

      if (!isStale()) {
        setPreview(null);
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  };

  const refreshCustomerSignaturePreview = (cSig: string | null | undefined) => {
    revokeSignatureBlobs();
    loadSignaturePreview(cSig, "customer", setCSignaturePreview, setCSignatureLoading);
  };

  const [customService, setCustomService] = useState("");
  const [customPart, setCustomPart] = useState("");
  const [customRemark, setCustomRemark] = useState("");
  const [machineTypeOptions, setMachineTypeOptions] = useState<string[]>([]);
  const [softwareOptions, setSoftwareOptions] = useState<string[]>([]);
  const [serviceOptions, setServiceOptions] = useState<string[]>([]);
  const [partOptions, setPartOptions] = useState<string[]>([]);
  const [remarkOptions, setRemarkOptions] = useState<string[]>([]);

  const [showCustomServiceInput, setShowCustomServiceInput] = useState(false);
  const [showCustomPartInput, setShowCustomPartInput] = useState(false);
  const [showCustomRemarkInput, setShowCustomRemarkInput] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const [openEditAfterViewClose, setOpenEditAfterViewClose] = useState(false);

  const showAlert = (
    status: "success" | "error" | "warning" | "info",
    message: string
  ) => {
    setCenterAlertStatus(status);
    setCenterAlertMessage(message);
    setCenterAlertVisible(true);

    if (centerAlertTimerRef.current) {
      clearTimeout(centerAlertTimerRef.current);
    }

    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
    }, 2500);
  };

  const {
    serviceCall,
    customerWisePagination,
    search,
    searchInput,
    setSearchInput,
    loading,
    pageSize,
    pageIndex,
    loadservices,
    handlePageChange,
    handlePageSizeChange,
  } = useCustomerWiseServiceCalls(() =>
    showAlert("error", "Unable to load service calls.")
  );

  /* ---------- Data loading ---------- */

  const loadCustomers = async () => {
    try {
      const response = await customerRepository.getCustomerIdNameAddressPlant();

      setCustomers(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Unable to load customers:", error);

      showAlert("error", "Unable to load customers.");
    }
  };

  const loadServiceDropdownOptions = async () => {
    try {
      const masterData = await settingRepository.getSetting(1, 1000, "");
      const loadValues = async (aliases: string[]) => {
        try {
          return await getMasterDataValues(aliases, masterData);
        } catch (error) {
          console.error(`Unable to load master data category "${aliases[0]}":`, error);
          return [];
        }
      };
      const [machineTypes, softwares, services, parts, remarks] = await Promise.all([
        loadValues([
          "Machine Type",
          "Machine Types",
          "Machine",
          "Machines",
          "Equipment Type",
        ]),
        loadValues([
          "Software",
          "Softwares",
          "Software Type",
          "Software Types",
          "Software Name",
        ]),
        loadValues(["Service", "Services", "Services Rendered", "Service Type"]),
        loadValues(["Part", "Parts", "Parts Replaced", "Spare Parts", "Part Type"]),
        loadValues(["Engineer Remark", "Engineer Remarks", "Remarks", "Remark"]),
      ]);
      setMachineTypeOptions(machineTypes);
      setSoftwareOptions(softwares);
      setServiceOptions(services);
      setPartOptions(parts);
      setRemarkOptions(remarks);
    } catch (error) {
      console.error("Unable to load service-call report dropdown master data:", error);
      showAlert(
        "error",
        error instanceof Error
          ? error.message
          : "Unable to load service-call report dropdown options."
      );
    }
  };

  useEffect(() => {
    loadCustomers();
    loadServiceDropdownOptions();
  }, []);

  // Open the edit drawer only after the View modal has closed.
  // This avoids Chakra UI modal scroll-lock interfering with the Drawer.
  useEffect(() => {
    if (openEditAfterViewClose && !isViewOpen) {
      setOpenEditAfterViewClose(false);
      onOpen();
    }
  }, [openEditAfterViewClose, isViewOpen, onOpen]);

  const handleViewServiceCall = (customer: any) => {
    setSelectedCustomer(customer);
    onViewOpen();
  };

  /* ---------- Form handlers ---------- */

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target;

    setForm((prev) => ({ ...prev, [id]: value }));

    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const customerId = e.target.value;

    const selected = customers.find((customer) => customer.id === customerId);

    setForm((prev) => ({
      ...prev,
      customerId,
      customerName: selected?.customerName || "",
      location: selected?.address || "",
      plant: selected?.plant || "",
    }));

    setErrors((prev) => ({
      ...prev,
      customerName: "",
      location: "",
      plant: "",
    }));
  };

  const handleMachineChange = (value: string) => {
    if (!value) return;

    setForm((prev) => ({
      ...prev,
      machineTypes: prev.machineTypes.includes(value)
        ? prev.machineTypes
        : [...prev.machineTypes, value],
    }));

    setErrors((prev) => ({ ...prev, machineTypes: "" }));
  };

  const removeMachine = (index: number) => {
    setForm((prev) => ({
      ...prev,
      machineTypes: prev.machineTypes.filter((_, i) => i !== index),
    }));
  };

  const handleSoftwareChange = (value: string) => {
    if (!value) return;

    setForm((prev) => ({
      ...prev,
      softwares: prev.softwares.includes(value)
        ? prev.softwares
        : [...prev.softwares, value],
    }));

    setErrors((prev) => ({ ...prev, softwares: "" }));
  };

  const removeSoftware = (index: number) => {
    setForm((prev) => ({
      ...prev,
      softwares: prev.softwares.filter((_, i) => i !== index),
    }));
  };

  const handleServiceChange = (value: string) => {
    if (!value) return;

    if (value === CUSTOM_SERVICE) {
      setCustomService("");
      setShowCustomServiceInput(true);
      return;
    }

    setForm((prev) => ({
      ...prev,
      servicesRendered: prev.servicesRendered.includes(value)
        ? prev.servicesRendered
        : [...prev.servicesRendered, value],
    }));
  };

  const addCustomService = () => {
    const value = customService.trim();

    if (!value) {
      showAlert("warning", "Please enter custom service.");
      return;
    }

    setForm((prev) => ({
      ...prev,
      servicesRendered: prev.servicesRendered.includes(value)
        ? prev.servicesRendered
        : [...prev.servicesRendered, value],
    }));

    setCustomService("");
    setShowCustomServiceInput(false);
  };

  const removeService = (index: number) => {
    setForm((prev) => ({
      ...prev,
      servicesRendered: prev.servicesRendered.filter((_, i) => i !== index),
    }));
  };

  const handlePartChange = (value: string) => {
    if (!value) return;

    if (value === CUSTOM_PART) {
      setCustomPart("");
      setShowCustomPartInput(true);
      return;
    }

    setForm((prev) => ({
      ...prev,
      partsReplaced: prev.partsReplaced.includes(value)
        ? prev.partsReplaced
        : [...prev.partsReplaced, value],
    }));
  };

  const addCustomPart = () => {
    const value = customPart.trim();

    if (!value) {
      showAlert("warning", "Please enter custom part.");
      return;
    }

    setForm((prev) => ({
      ...prev,
      partsReplaced: prev.partsReplaced.includes(value)
        ? prev.partsReplaced
        : [...prev.partsReplaced, value],
    }));

    setCustomPart("");
    setShowCustomPartInput(false);
  };

  const removePart = (index: number) => {
    setForm((prev) => ({
      ...prev,
      partsReplaced: prev.partsReplaced.filter((_, i) => i !== index),
    }));
  };

  const handleRemarkChange = (value: string) => {
    if (!value) return;

    if (value === CUSTOM_REMARK) {
      setCustomRemark("");
      setShowCustomRemarkInput(true);
      return;
    }

    setForm((prev) => ({
      ...prev,
      engineerRemarks: prev.engineerRemarks.includes(value)
        ? prev.engineerRemarks
        : [...prev.engineerRemarks, value],
    }));
  };

  const addCustomRemark = () => {
    const value = customRemark.trim();

    if (!value) {
      showAlert("warning", "Please enter custom remark.");
      return;
    }

    setForm((prev) => ({
      ...prev,
      engineerRemarks: prev.engineerRemarks.includes(value)
        ? prev.engineerRemarks
        : [...prev.engineerRemarks, value],
    }));

    setCustomRemark("");
    setShowCustomRemarkInput(false);
  };

  const removeRemark = (index: number) => {
    setForm((prev) => ({
      ...prev,
      engineerRemarks: prev.engineerRemarks.filter((_, i) => i !== index),
    }));
  };

  const handleReset = () => {
    setForm(emptyForm());
    setErrors({});
    setIsEdit(false);
    setEditingId("");

    setCustomService("");
    setCustomPart("");
    setCustomRemark("");

    setShowCustomServiceInput(false);
    setShowCustomPartInput(false);
    setShowCustomRemarkInput(false);

    revokeSignatureBlobs();

    // invalidate any in-flight preview fetches
    previewVersionRef.current.customer++;

    setCSignaturePreview(null);
    setCSignatureLoading(false);

    closeSignatureDrawer();
  };

  const handleAdd = () => {
    handleReset();
    onOpen();
  };

  const handleEdit = async (item: ServiceCall) => {
    try {
      const id = (item as any).id || (item as any).serviceCallId;

      if (!id) {
        showAlert("error", "Service Call ID not found.");
        return;
      }

      let serviceCall: any = item;

      try {
        const latest = await serviceCallRepository.getById(id);

        if (latest) {
          serviceCall = latest;
        }
      } catch (error) {
        console.error("Unable to get latest service call:", error);
      }

      setIsEdit(true);
      setEditingId(id);

      const cleanCSignature = hasSignatureValue(
        serviceCall.customerSignatureUrl || serviceCall.cSignature
      )
        ? (serviceCall.customerSignatureUrl || (serviceCall.cSignature as string)).trim()
        : "";

      const serviceData: ServiceCallForm = {
        customerId: serviceCall.customerId || "",
        customerName: serviceCall.customerName || "",
        location: serviceCall.location || "",

        machineTypes: Array.isArray(serviceCall.machineTypes) ? serviceCall.machineTypes : [],
        softwares: Array.isArray(serviceCall.softwares) ? serviceCall.softwares : [],

        visitFor: Number(serviceCall.visitFor || 0),

        servicesRendered: Array.isArray(serviceCall.servicesRendered)
          ? serviceCall.servicesRendered
          : [],
        partsReplaced: Array.isArray(serviceCall.partsReplaced) ? serviceCall.partsReplaced : [],
        engineerRemarks: Array.isArray(serviceCall.engineerRemarks)
          ? serviceCall.engineerRemarks
          : [],

        customerRemark: serviceCall.customerRemark || "",

        customerEngineer: serviceCall.customerEngineer || "",
        customerEngineerDesignation: serviceCall.customerEngineerDesignation || "",

        cSignature: cleanCSignature,

        plant: serviceCall.plant || "",
        status: serviceCall.status || "Open",
      };

      setForm(serviceData);

      const customer = customers.find(
        (c) =>
          c.id === serviceCall.customerId || c.customerName === serviceCall.customerName
      );

      if (customer) {
        setForm((prev) => ({
          ...prev,
          customerId: customer.id,
          customerName: customer.customerName,
          location: customer.address || serviceCall.location || "",
          plant: customer.plant || serviceCall.plant || "",
        }));
      }

      refreshCustomerSignaturePreview(cleanCSignature);

      if (isViewOpen) {
        setOpenEditAfterViewClose(true);
        onViewClose();
      } else {
        onOpen();
      }
    } catch (error) {
      console.error("Edit error:", error);

      showAlert("error", "Unable to edit service call.");
    }
  };

  /* ---------- Payload builders ---------- */

  const buildFormData = (): FormData => {
    const formData = new FormData();

    formData.append("CustomerId", form.customerId || "");
    formData.append("CustomerName", form.customerName || "");
    formData.append("Location", form.location || "");
    formData.append("VisitFor", String(Number(form.visitFor || 0)));
    formData.append("CustomerRemark", form.customerRemark || "");
    formData.append("CustomerEngineer", form.customerEngineer || "");
    formData.append(
      "CustomerEngineerDesignation",
      form.customerEngineerDesignation || ""
    );
    formData.append("Plant", form.plant || "");
    formData.append("Status", form.status || "Open");

    form.machineTypes.forEach((machineType) => {
      if (machineType) {
        formData.append("MachineTypes", machineType);
      }
    });

    form.softwares.forEach((software) => {
      if (software) {
        formData.append("Softwares", software);
      }
    });

    form.servicesRendered.forEach((service) => {
      if (service && service !== CUSTOM_SERVICE) {
        formData.append("ServicesRendered", service);
      }
    });

    form.partsReplaced.forEach((part) => {
      if (part && part !== CUSTOM_PART) {
        formData.append("PartsReplaced", part);
      }
    });

    form.engineerRemarks.forEach((remark) => {
      if (remark && remark !== CUSTOM_REMARK) {
        formData.append("EngineerRemarks", remark);
      }
    });

    if (form.cSignature) {
      formData.append("CSignature", form.cSignature);

      const customerFile = dataUrlToFile(form.cSignature, "customer-signature.png");

      if (customerFile) {
        formData.append("CSignatureFile", customerFile);
      }
    }

    // Explicitly send empty engineer signature to prevent backend auto-fill
    formData.append("ESignature", "");

    return formData;
  };

  const buildUpdatePayload = (): ServiceCallUpdate & {
    CSignatureFile?: File | null;
  } => {
    const cleanMachineTypes = form.machineTypes.filter((m) => !!m);
    const cleanSoftwares = form.softwares.filter((s) => !!s);
    const cleanServices = form.servicesRendered.filter((s) => !!s && s !== CUSTOM_SERVICE);
    const cleanParts = form.partsReplaced.filter((p) => !!p && p !== CUSTOM_PART);
    const cleanRemarks = form.engineerRemarks.filter((r) => !!r && r !== CUSTOM_REMARK);

    const payload: ServiceCallUpdate & {
      CSignatureFile?: File | null;
    } = {
      customerId: form.customerId || undefined,
      customerName: form.customerName || undefined,
      location: form.location?.trim() || undefined,

      machineTypes: cleanMachineTypes.length ? cleanMachineTypes : undefined,
      softwares: cleanSoftwares.length ? cleanSoftwares : undefined,

      visitFor: Number.isFinite(form.visitFor) ? Number(form.visitFor) : 0,

      servicesRendered: cleanServices.length ? cleanServices : undefined,
      partsReplaced: cleanParts.length ? cleanParts : undefined,
      engineerRemarks: cleanRemarks.length ? cleanRemarks : undefined,

      customerRemark: form.customerRemark || undefined,
      customerEngineer: form.customerEngineer || undefined,
      customerEngineerDesignation: form.customerEngineerDesignation || undefined,

      cSignature: form.cSignature || undefined,

      plant: form.plant?.trim() || undefined,
      status: form.status || "Open",
    };

    if (form.cSignature) {
      payload.CSignatureFile = dataUrlToFile(form.cSignature, "customer-signature.png");
    }

    return payload;
  };

  const handleSubmit = async () => {
    const validationErrors: Record<string, string> = {};

    if (!form.customerId) {
      validationErrors.customerName = "Customer is required.";
    }

    if (!form.location.trim()) {
      validationErrors.location = "Address is required.";
    }

    if (!form.machineTypes.length) {
      validationErrors.machineTypes = "Select at least one machine type.";
    }

    if (!form.softwares.length) {
      validationErrors.softwares = "Select at least one software.";
    }

    if (!form.plant.trim()) {
      validationErrors.plant = "Plant is required.";
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);

      showAlert("warning", "Please fill all required fields.");

      return;
    }

    setErrors({});

    try {
      setIsSubmitting(true);

      if (!isEdit) {
        const formData = buildFormData();

        await serviceCallRepository.add(formData);

        showAlert("success", "Service Call Added Successfully");
      } else {
        await serviceCallRepository.update(editingId, buildUpdatePayload());

        showAlert("success", "Service Call Updated Successfully");
      }

      handleReset();

      onClose();

      // Reload current customer-wise page.
      await loadservices(pageIndex + 1, pageSize, search);
    } catch (error: any) {
      console.error("Service call save error:", error);

      let message = "Unable to Save Service Call.";

      if (error?.response?.data?.errors) {
        const backendErrors = error.response.data.errors;

        const messages = Object.keys(backendErrors).flatMap((key) =>
          Array.isArray(backendErrors[key])
            ? backendErrors[key]
            : [String(backendErrors[key])]
        );

        if (messages.length) {
          message = messages.join(" ");
        }
      } else if (typeof error?.response?.data === "string") {
        message = error.response.data;
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }

      showAlert("error", message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- Download ---------- */

  const triggerPdfDownload = (blob: Blob, id: string) => {
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = `ServiceCallReport_${id}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };


  const downloadReport = async (id: string) => {
    if (!id) {
      showAlert("error", "Service Call ID not found.");
      return;
    }

    try {
      setIsDownloadingReport(true);
      const blob = await serviceCallRepository.download(id);
      triggerPdfDownload(blob, id);
    } catch (error) {
      console.error("Download error:", error);

      showAlert("error", "Unable to download service call report.");
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleDownload = async (item: ServiceCall) => {
    const id = item.id || item.serviceCallId;
    await downloadReport(id);
  };

  const clearReportPreview = () => {
    if (reportPreviewUrlRef.current) {
      URL.revokeObjectURL(reportPreviewUrlRef.current);
      reportPreviewUrlRef.current = null;
    }
    setReportPreviewUrl(null);
    setPreviewedReportId("");
  };

  const closeServiceCallPopup = () => {
    clearReportPreview();
    onViewClose();
  };

  const handlePreviewReport = async (item: ServiceCall) => {
    const id = item.id || item.serviceCallId;
    if (!id) {
      showAlert("error", "Service Call ID not found.");
      return;
    }

    try {
      setPreviewingReportId(id);
      const blob = await serviceCallRepository.download(id);
      const pdfBlob = blob.type === "application/pdf"
        ? blob
        : new Blob([blob], { type: "application/pdf" });
      clearReportPreview();
      const objectUrl = URL.createObjectURL(pdfBlob);
      reportPreviewUrlRef.current = objectUrl;
      setReportPreviewUrl(objectUrl);
      setPreviewedReportId(id);
    } catch (error) {
      console.error("Report preview error:", error);
      showAlert("error", "Unable to preview service call report.");
    } finally {
      setPreviewingReportId("");
    }
  };

  const handleSendReportEmail = async () => {
    const serviceCallId = previewedReportId;
    if (!serviceCallId) {
      showAlert("error", "Service Call ID not found.");
      return;
    }

    try {
      setIsSendingReportEmail(true);
      await serviceCallRepository.sendReportEmail(serviceCallId);
      showAlert("success", "Service call report sent by email.");
    } catch (error: any) {
      console.error("Unable to send service call report email:", error);
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : "") ||
        "Unable to send service call report by email.";
      showAlert("error", message);
    } finally {
      setIsSendingReportEmail(false);
    }
  };

  useEffect(
    () => () => {
      if (reportPreviewUrlRef.current) {
        URL.revokeObjectURL(reportPreviewUrlRef.current);
      }
    },
    []
  );

  /* ---------- Signature pad ---------- */

  const openSignatureDrawer = (type: SignatureType) => {
    setSignatureType(type);
    setIsSignatureOpen(true);
  };

  const closeSignatureDrawer = () => {
    setIsSignatureOpen(false);
    setSignatureType(null);
    isDrawingRef.current = false;
  };

  useEffect(() => {
    if (!isSignatureOpen || !signatureType) {
      return;
    }

    const timer = window.setTimeout(() => {
      const canvas = signatureCanvasRef.current;

      if (!canvas) return;

      const ctx = canvas.getContext("2d");

      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000000";

      const existing = cSignaturePreview;

      if (existing) {
        const image = new Image();

        image.onload = () => {
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        };

        image.onerror = () => {
          console.error(
            `Failed to draw existing ${signatureType} signature onto canvas. Preview src:`,
            existing
          );
        };

        image.src = existing;
      }
    }, 150);

    return () => window.clearTimeout(timer);
  }, [isSignatureOpen, signatureType, cSignaturePreview]);

  const getCanvasCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const canvas = signatureCanvasRef.current;

    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();

    let clientX = 0;
    let clientY = 0;

    if ("touches" in e) {
      if (!e.touches.length) {
        return null;
      }

      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    e.preventDefault();

    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    const position = getCanvasCoordinates(e);

    if (!canvas || !ctx || !position) {
      return;
    }

    ctx.beginPath();
    ctx.moveTo(position.x, position.y);

    isDrawingRef.current = true;
  };

  const drawSignature = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (!isDrawingRef.current) {
      return;
    }

    e.preventDefault();

    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    const position = getCanvasCoordinates(e);

    if (!canvas || !ctx || !position) {
      return;
    }

    ctx.lineTo(position.x, position.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!canvas || !ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;

    if (!canvas || !signatureType) {
      return;
    }

    const signature = canvas.toDataURL("image/png");

    const savedType = signatureType;

    previewVersionRef.current[savedType]++;

    setForm((prev) => ({ ...prev, cSignature: signature }));
    setCSignaturePreview(signature);
    setCSignatureLoading(false);

    closeSignatureDrawer();

    showAlert("success", "Customer signature saved successfully.");
  };

  /* ---------- Pagination data ---------- */

  const totalPages = Math.max(1, customerWisePagination.totalPages || 1);

  const createPages = () => Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <>
      {/* ===================== CENTER ALERT ===================== */}

      {centerAlertVisible && (
        <Center position="fixed" inset={0} zIndex={9999} pointerEvents="none">
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="500px"
            maxW="90vw"
            borderRadius="18px"
            py={8}
            px={8}
            boxShadow="2xl"
          >
            <AlertIcon boxSize="44px" mr={0} />

            <Text mt={3} fontWeight="bold" fontSize="lg">
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

      {/* ===================== MAIN ===================== */}

      <Box p={2} mt="70px" bg={pageBg} borderRadius="25px">
        <Card
          bg={cardBg}
          borderRadius="25px"
          overflow="hidden"
          boxShadow="0 10px 30px rgba(112,98,185,0.12)"
        >
          {/* ---------- HEADER ---------- */}
          <Flex justify="flex-end" align="center" p={6} flexWrap="wrap" gap={4}>
            <HStack spacing={3}>
              <InputGroup maxW="320px">
                <InputLeftElement h="44px" pointerEvents="none">
                  <SearchIcon color={PURPLE} />
                </InputLeftElement>

                <Input
                  h="44px"
                  borderRadius="14px"
                  bg={PURPLE_SOFT}
                  border="none"
                  color={NAVY}
                  _placeholder={{ color: MUTED }}
                  _focus={{ boxShadow: `0 0 0 2px ${PURPLE}` }}
                  placeholder="Search service calls..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </InputGroup>

              <Button
                leftIcon={<AddIcon boxSize="3" />}
                h="50px"
                minW="max-content"
                px={6}
                fontSize="md"
                whiteSpace="nowrap"
                flexShrink={0}
                borderRadius="14px"
                {...primaryBtn}
                boxShadow="0 6px 14px rgba(112,98,185,0.35)"
                onClick={handleAdd}
              >
                Add Service Call Report
              </Button>
            </HStack>
          </Flex>

          {/* ---------- TABLE ---------- */}
          <Box px={6} pb={2} w="100%" overflowX="auto">
            <Table
              variant="unstyled"
              w="100%"
              sx={{ borderCollapse: "separate", borderSpacing: "0 10px" }}
            >
              <Thead>
                <Tr>
                  <Th {...thStyle} w="40%">
                    Customer
                  </Th>
                  <Th {...thStyle} w="45%">
                    Address
                  </Th>
                  <Th {...thStyle} w="15%" textAlign="center">
                    View Service Call
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {serviceCall.length === 0 ? (
                  <Tr>
                    <Td colSpan={3} textAlign="center" py={10} border="none">
                      <Text color={mutedColor}>
                        {loading ? "Loading..." : search.trim()
                          ? "No service calls found for your search."
                          : "No Service Calls Found"}
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  serviceCall.map((call, callIndex) => {
                    const base = callIndex % 2 === 0 ? rowBg : rowAltBg;

                    return (
                      <Tr
                        key={call.customerName || callIndex}
                        cursor="pointer"
                        onClick={() => handleViewServiceCall(call)}
                        sx={{
                          "& > td": {
                            bg: base,
                            boxShadow: rowShadow,
                            border: "none",
                            color: pageText,
                            transition: "background 0.15s ease, color 0.15s ease",
                          },
                          "& > td:first-of-type": { borderLeftRadius: "14px" },
                          "& > td:last-of-type": { borderRightRadius: "14px" },
                        }}
                        _hover={{
                          "& > td": {
                            bg: `linear-gradient(90deg, ${PURPLE} 0%, #8A7DD0 100%)`,
                            color: "white",
                          },
                          "& .row-action": { color: "white" },
                        }}
                      >
                        {/* CUSTOMER */}
                        <Td verticalAlign="middle" py={4}>
                          <HStack spacing={3}>
                            <Avatar
                              size="sm"
                              name={call.customerName || "Customer"}
                              bg={PURPLE_SOFT}
                              color={PURPLE}
                              flexShrink={0}
                            />

                            <Text
                              fontWeight="600"
                              fontSize="sm"
                              whiteSpace="normal"
                              wordBreak="break-word"
                            >
                              {call.customerName}
                            </Text>
                          </HStack>
                        </Td>

                        {/* ADDRESS */}
                        <Td verticalAlign="middle" py={4}>
                          <Text fontSize="sm" whiteSpace="normal" wordBreak="break-word">
                            {call.address || "-"}
                          </Text>
                        </Td>

                        {/* VIEW */}
                        <Td textAlign="center" py={4}>
                          <IconButton
                            className="row-action"
                            icon={<ViewIcon />}
                            aria-label="View service calls"
                            size="sm"
                            variant="ghost"
                            color={PURPLE}
                            _hover={{ bg: "whiteAlpha.300" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewServiceCall(call);
                            }}
                          />
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          {/* ---------- FOOTER / PAGINATION ---------- */}
          <Flex
            w="100%"
            justify="space-between"
            align="center"
            px={6}
            pb={6}
            pt={2}
            flexWrap="wrap"
            gap={3}
          >
            <HStack spacing={3}>
              <Text fontSize="13px" color={mutedColor}>
                {customerWisePagination.totalCount > 0
                  ? `${pageIndex * pageSize + 1}-${Math.min(
                      (pageIndex + 1) * pageSize,
                      customerWisePagination.totalCount
                    )} of ${customerWisePagination.totalCount} results`
                  : "0 results"}
              </Text>

              <Menu>
                <Text fontSize="13px" color={mutedColor}>
                  Rows per page
                </Text>

                <MenuButton
                  as={Button}
                  size="sm"
                  rightIcon={<ChevronDownIcon />}
                  bg={PURPLE_SOFT}
                  color={PURPLE}
                  borderRadius="10px"
                  _hover={{ bg: "#E2DDF6" }}
                  _active={{ bg: "#E2DDF6" }}
                >
                  {pageSize}
                </MenuButton>

                <MenuList zIndex={1000} boxShadow="lg" borderRadius="12px">
                  {[10, 20, 50].map((size) => (
                    <MenuItem key={size} onClick={() => handlePageSizeChange(size)}>
                      {size}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </HStack>

            <HStack spacing={1}>
              <IconButton
                aria-label="Previous page"
                icon={<MdChevronLeft size={18} />}
                variant="ghost"
                size="sm"
                color={btnColor}
                isDisabled={pageIndex === 0}
                onClick={() => handlePageChange(pageIndex - 1)}
              />

              {createPages().map((num) => {
                const active = num === pageIndex + 1;

                return (
                  <Button
                    key={num}
                    size="sm"
                    minW="32px"
                    h="32px"
                    borderRadius="10px"
                    fontWeight={active ? 700 : 500}
                    bg={active ? PURPLE : "transparent"}
                    color={active ? "white" : btnColor}
                    _hover={{ bg: active ? PURPLE_DARK : PURPLE_SOFT }}
                    onClick={() => handlePageChange(num - 1)}
                  >
                    {num}
                  </Button>
                );
              })}

              <IconButton
                aria-label="Next page"
                icon={<MdChevronRight size={18} />}
                variant="ghost"
                size="sm"
                color={btnColor}
                isDisabled={pageIndex >= totalPages - 1}
                onClick={() => handlePageChange(pageIndex + 1)}
              />
            </HStack>
          </Flex>
        </Card>

        {/* ===================== SERVICE CALL DRAWER ===================== */}

        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={onClose}
          size="xl"
          blockScrollOnMount={true}
          preserveScrollBarGap={true}
        >
          <DrawerOverlay bg="rgba(0, 0, 0, 0.25)" />

          <DrawerContent
            h="100vh"
            maxH="100vh"
            minH={0}
            borderTopLeftRadius="24px"
            borderBottomLeftRadius="24px"
            overflow="hidden"
          >
            <DrawerCloseButton mt={3} color="white" zIndex={10} />

            <DrawerHeader bg={HEADER_GRADIENT} color="white" py={6}>
              <Text fontSize="2xl" fontWeight="700">
                {isEdit ? "Generate Service Call" : "Add Service Call Report"}
              </Text>

              <Text fontSize="sm" opacity={0.9} mt={1}>
                Fill the information below.
              </Text>
            </DrawerHeader>

            <DrawerBody
              py={6}
              flex="1"
              minH={0}
              overflowY="auto"
              overflowX="hidden"
              overscrollBehavior="contain"
              sx={{
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-thumb": { borderRadius: "8px" },
              }}
            >
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                }}
                gap={5}
              >
                {/* CUSTOMER */}
                <FormControl isInvalid={!!errors.customerName}>
                  <FieldLabel>Customer</FieldLabel>

                  <Select
                    placeholder="Select Customer"
                    value={form.customerId}
                    onChange={handleCustomerChange}
                    {...fieldProps}
                  >
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.customerName}
                      </option>
                    ))}
                  </Select>

                  <FormErrorMessage>{errors.customerName}</FormErrorMessage>
                </FormControl>

                {/* ADDRESS */}
                <FormControl isInvalid={!!errors.location}>
                  <FieldLabel>Address</FieldLabel>

                  <Input
                    value={form.location}
                    isReadOnly
                    {...fieldProps}
                    placeholder="Address will appear automatically"
                  />

                  <FormErrorMessage>{errors.location}</FormErrorMessage>
                </FormControl>

                {/* MACHINE */}
                <FormControl isInvalid={!!errors.machineTypes}>
                  <FieldLabel>Machine Type</FieldLabel>

                  <Select
                    placeholder="Select Machine Type"
                    value=""
                    onChange={(e) => handleMachineChange(e.target.value)}
                    {...fieldProps}
                  >
                    {machineTypeOptions.map((machine) => (
                      <option key={machine} value={machine}>
                        {machine}
                      </option>
                    ))}
                  </Select>

                  <RemovableList
                    items={form.machineTypes}
                    onRemove={removeMachine}
                    borderColor={border}
                  />

                  <FormErrorMessage>{errors.machineTypes}</FormErrorMessage>
                </FormControl>

                {/* SOFTWARE */}
                <FormControl isInvalid={!!errors.softwares}>
                  <FieldLabel>Software</FieldLabel>

                  <Select
                    placeholder="Select Software"
                    value=""
                    onChange={(e) => handleSoftwareChange(e.target.value)}
                    {...fieldProps}
                  >
                    {softwareOptions.map((software) => (
                      <option key={software} value={software}>
                        {software}
                      </option>
                    ))}
                  </Select>

                  <RemovableList
                    items={form.softwares}
                    onRemove={removeSoftware}
                    borderColor={border}
                  />

                  <FormErrorMessage>{errors.softwares}</FormErrorMessage>
                </FormControl>

                {/* VISIT FOR */}
                <FormControl>
                  <FieldLabel>Visit For</FieldLabel>

                  <Select
                    value={form.visitFor}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        visitFor: Number(e.target.value),
                      }))
                    }
                    {...fieldProps}
                  >
                    <option value={0}>General</option>
                    <option value={1}>Installation</option>
                    <option value={2}>Service</option>
                    <option value={3}>Repair</option>
                  </Select>
                </FormControl>

                {/* PLANT */}
                <FormControl isInvalid={!!errors.plant}>
                  <FieldLabel>Plant</FieldLabel>

                  <Input
                    id="plant"
                    placeholder="Enter plant"
                    value={form.plant}
                    onChange={handleChange}
                    {...fieldProps}
                  />

                  <FormErrorMessage>{errors.plant}</FormErrorMessage>
                </FormControl>

                {/* SERVICES */}
                <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                  <FieldLabel>Services Rendered</FieldLabel>

                  <Select
                    placeholder="Select Service"
                    value=""
                    onChange={(e) => handleServiceChange(e.target.value)}
                    {...fieldProps}
                  >
                    {serviceOptions.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}

                    <option value={CUSTOM_SERVICE}>+ Add Custom Service</option>
                  </Select>

                  {showCustomServiceInput && (
                    <>
                      <Input
                        mt={3}
                        placeholder="Enter custom service"
                        value={customService}
                        onChange={(e) => setCustomService(e.target.value)}
                        {...fieldProps}
                      />

                      <Button mt={2} size="sm" {...outlineBtn} onClick={addCustomService}>
                        Add Custom Service
                      </Button>
                    </>
                  )}

                  <RemovableList
                    items={form.servicesRendered}
                    onRemove={removeService}
                    borderColor={border}
                  />
                </FormControl>

                {/* PARTS */}
                <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                  <FieldLabel>Parts Replaced</FieldLabel>

                  <Select
                    placeholder="Select Part"
                    value=""
                    onChange={(e) => handlePartChange(e.target.value)}
                    {...fieldProps}
                  >
                    {partOptions.map((part) => (
                      <option key={part} value={part}>
                        {part}
                      </option>
                    ))}

                    <option value={CUSTOM_PART}>+ Add Custom Part</option>
                  </Select>

                  {showCustomPartInput && (
                    <>
                      <Input
                        mt={3}
                        placeholder="Enter custom part"
                        value={customPart}
                        onChange={(e) => setCustomPart(e.target.value)}
                        {...fieldProps}
                      />

                      <Button mt={2} size="sm" {...outlineBtn} onClick={addCustomPart}>
                        Add Custom Part
                      </Button>
                    </>
                  )}

                  <RemovableList
                    items={form.partsReplaced}
                    onRemove={removePart}
                    borderColor={border}
                  />
                </FormControl>

                {/* REMARKS */}
                <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                  <FieldLabel>Engineer Remarks</FieldLabel>

                  <Select
                    placeholder="Select Remark"
                    value=""
                    onChange={(e) => handleRemarkChange(e.target.value)}
                    {...fieldProps}
                  >
                    {remarkOptions.map((remark) => (
                      <option key={remark} value={remark}>
                        {remark}
                      </option>
                    ))}

                    <option value={CUSTOM_REMARK}>+ Add Custom Remark</option>
                  </Select>

                  {showCustomRemarkInput && (
                    <>
                      <Input
                        mt={3}
                        placeholder="Enter custom remark"
                        value={customRemark}
                        onChange={(e) => setCustomRemark(e.target.value)}
                        {...fieldProps}
                      />

                      <Button mt={2} size="sm" {...outlineBtn} onClick={addCustomRemark}>
                        Add Custom Remark
                      </Button>
                    </>
                  )}

                  <RemovableList
                    items={form.engineerRemarks}
                    onRemove={removeRemark}
                    borderColor={border}
                  />
                </FormControl>

                {/* CUSTOMER REMARK */}
                <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                  <FieldLabel>Customer Remark</FieldLabel>

                  <Textarea
                    id="customerRemark"
                    placeholder="Enter customer remark"
                    value={form.customerRemark}
                    onChange={handleChange}
                    {...fieldProps}
                  />
                </FormControl>

                {/* CUSTOMER ENGINEER */}
                <FormControl>
                  <FieldLabel>Customer Engineer</FieldLabel>

                  <Input
                    id="customerEngineer"
                    placeholder="Enter customer engineer"
                    value={form.customerEngineer}
                    onChange={handleChange}
                    {...fieldProps}
                  />
                </FormControl>

                {/* DESIGNATION */}
                <FormControl>
                  <FieldLabel>Customer Engineer Designation</FieldLabel>

                  <Input
                    id="customerEngineerDesignation"
                    placeholder="Enter designation"
                    value={form.customerEngineerDesignation}
                    onChange={handleChange}
                    {...fieldProps}
                  />
                </FormControl>

                {/* CUSTOMER SIGNATURE */}
                <FormControl>
                  <FieldLabel>Customer Signature</FieldLabel>

                  <HStack spacing={3} align="center">
                    <Button
                      borderRadius="12px"
                      {...primaryBtn}
                      onClick={() => openSignatureDrawer("customer")}
                    >
                      {hasSignatureValue(form.cSignature) ? "Edit Signature" : "Add Signature"}
                    </Button>

                    <SignaturePreviewBox
                      src={cSignaturePreview}
                      loading={cSignatureLoading}
                      hasValue={hasSignatureValue(form.cSignature)}
                      alt="Customer Signature"
                      borderColor={inputBorderColor}
                    />
                  </HStack>
                </FormControl>

                {/* STATUS */}
                <FormControl>
                  <FieldLabel>Status</FieldLabel>

                  <Select id="status" value={form.status} onChange={handleChange} {...fieldProps}>
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Closed">Closed</option>
                  </Select>
                </FormControl>
              </Grid>
            </DrawerBody>

            <DrawerFooter borderTop="1px solid" borderColor={border} gap={3}>
              <Button variant="outline" borderRadius="12px" onClick={handleReset}>
                Reset
              </Button>

              <Button
                borderRadius="12px"
                {...primaryBtn}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={isDownloadingReport}
              >
                Save Service Call Report
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        {/* ===================== VIEW SERVICE CALLS ===================== */}

        <Modal
          isOpen={isViewOpen}
          onClose={closeServiceCallPopup}
          size={reportPreviewUrl ? "6xl" : "4xl"}
          isCentered
          scrollBehavior="inside"
        >
          <ModalOverlay bg="blackAlpha.500" />

          <ModalContent
            borderRadius="20px"
            overflow="hidden"
            h={reportPreviewUrl ? "90vh" : undefined}
            maxW={reportPreviewUrl ? "90vw" : undefined}
          >
            <ModalHeader bg={HEADER_GRADIENT} color="white">
              {reportPreviewUrl ? (
                <Flex align="center" gap={3} pr={8}>
                  <Button
                    size="sm"
                    variant="outline"
                    color="white"
                    borderColor="whiteAlpha.700"
                    leftIcon={<MdChevronLeft />}
                    onClick={clearReportPreview}
                  >
                    Back to service calls
                  </Button>
                  <Text fontSize="xl" fontWeight="700">
                    Service Call Report Preview
                  </Text>
                </Flex>
              ) : (
                <>
                  <Text fontSize="xl" fontWeight="700">
                    Service Call Report Details
                  </Text>
                  <Text fontSize="sm" fontWeight="normal" opacity={0.9} mt={1}>
                    {selectedCustomer?.customerName || "Customer"}
                  </Text>
                </>
              )}
            </ModalHeader>

            <ModalCloseButton color="white" />

            <ModalBody
              position="relative"
              py={reportPreviewUrl ? 0 : 6}
              px={reportPreviewUrl ? 0 : 6}
              display="flex"
              flexDirection="column"
              minH={reportPreviewUrl ? 0 : undefined}
              flex={reportPreviewUrl ? 1 : undefined}
              bg={reportPreviewUrl ? "gray.100" : undefined}
            >
              {reportPreviewUrl ? (
                <iframe
                  title="Service Call Report PDF Preview"
                  src={reportPreviewUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0, flex: 1, minHeight: 0 }}
                />
              ) : (
                <>
              {/* CUSTOMER INFO */}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
                mb={6}
              >
                <Box
                  p={4}
                  border="1px solid"
                  borderColor={border}
                  borderRadius="12px"
                  bg={tableBg}
                >
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Customer Name
                  </Text>

                  <Text fontSize="md" fontWeight="600" color={textColor}>
                    {selectedCustomer?.customerName || "-"}
                  </Text>
                </Box>

                <Box
                  p={4}
                  border="1px solid"
                  borderColor={border}
                  borderRadius="12px"
                  bg={tableBg}
                >
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Address
                  </Text>

                  <Text fontSize="md" fontWeight="600" color={textColor}>
                    {selectedCustomer?.address || "-"}
                  </Text>
                </Box>
              </Grid>

              <Text fontSize="lg" fontWeight="700" mb={3} color={textColor}>
                Service Calls
              </Text>

              <Box
                border="1px solid"
                borderColor={border}
                borderRadius="12px"
                overflowX="auto"
              >
                <Table variant="simple">
                  <Thead bg={headerBg}>
                    <Tr>
                      <Th color="white">#</Th>
                      <Th color="white">CREATED BY</Th>
                      <Th color="white">CREATED AT</Th>
                      <Th color="white" textAlign="center">
                        ACTION
                      </Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {selectedCustomer?.serviceCalls?.length > 0 ? (
                      selectedCustomer.serviceCalls.map((call: any, index: number) => (
                        <Tr key={call.id || index} _hover={{ bg: hoverBg }}>
                          <Td>{index + 1}</Td>

                          <Td>
                            <Text fontSize="sm">{call.createdBy || "-"}</Text>
                          </Td>

                          <Td>
                            <Text fontSize="sm">
                              {call.createdAt
                                ? new Date(call.createdAt).toLocaleString("en-IN")
                                : "-"}
                            </Text>
                          </Td>

                          <Td borderColor={border} textAlign="center">
                            <HStack spacing={1} justify="center">
                              <Tooltip
                                label={
                                  !hasGeneratedReport(call.reportGenerated)
                                    ? "Generate Service Call Report"
                                    : "Edit service call"
                                }
                                hasArrow
                              >
                                <IconButton
                                  aria-label={
                                    !hasGeneratedReport(call.reportGenerated)
                                      ? "Generate Service Call Report"
                                      : "Edit Service Call"
                                  }
                                  icon={<EditIcon />}
                                  color={PURPLE}
                                  variant="ghost"
                                  rounded="full"
                                  _hover={{ bg: PURPLE_SOFT }}
                                  onClick={() => handleEdit(call)}
                                />
                              </Tooltip>

                              {(call.id || call.serviceCallId) &&
                                hasGeneratedReport(call.reportGenerated) && (
                                  <>
                                    <Button
                                      size="sm"
                                      leftIcon={<ViewIcon />}
                                      colorScheme="purple"
                                      variant="ghost"
                                      onClick={() => handlePreviewReport(call)}
                                      isLoading={
                                        previewingReportId ===
                                        (call.id || call.serviceCallId)
                                      }
                                      isDisabled={
                                        !!previewingReportId &&
                                        previewingReportId !==
                                          (call.id || call.serviceCallId)
                                      }
                                    >
                                      Preview Report
                                    </Button>
                                    <Tooltip label="Download service call report" hasArrow>
                                      <IconButton
                                        aria-label="Download Service Call Report"
                                        icon={<DownloadIcon />}
                                        colorScheme="green"
                                        variant="ghost"
                                        rounded="full"
                                        onClick={() => handleDownload(call)}
                                      />
                                    </Tooltip>
                                  </>
                                )}
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={4} textAlign="center" py={8}>
                          <Text color="gray.500">No service calls found.</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
                </>
              )}
              {previewingReportId && (
                <Flex
                  position="absolute"
                  inset={0}
                  zIndex={1}
                  bg="whiteAlpha.800"
                  direction="column"
                  align="center"
                  justify="center"
                  gap={4}
                  aria-live="polite"
                >
                  <Spinner size="xl" thickness="5px" color={PURPLE} />
                  <Text fontSize="lg" fontWeight="600" color={NAVY}>
                    Loading report preview...
                  </Text>
                </Flex>
              )}
            </ModalBody>

            <ModalFooter borderTop="1px solid" borderColor={border}>
              {reportPreviewUrl ? (
                <HStack spacing={3}>
                  <Button
                    borderRadius="12px"
                    variant="outline"
                    leftIcon={<EmailIcon />}
                    onClick={handleSendReportEmail}
                    isLoading={isSendingReportEmail}
                    isDisabled={!previewedReportId}
                  >
                    Send to Email
                  </Button>
                  <Button
                    borderRadius="12px"
                    {...primaryBtn}
                    onClick={clearReportPreview}
                  >
                    Back to service calls
                  </Button>
                </HStack>
              ) : (
                <Button borderRadius="12px" {...primaryBtn} onClick={closeServiceCallPopup}>
                  Close
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* ===================== SIGNATURE MODAL ===================== */}

        <Modal
          isOpen={isSignatureOpen}
          onClose={closeSignatureDrawer}
          isCentered
          size="lg"
          closeOnOverlayClick={false}
        >
          <ModalOverlay bg="rgba(0, 0, 0, 0.18)" />

          <ModalContent borderRadius="20px" overflow="hidden" mx={4} boxShadow="2xl">
            <ModalHeader bg={HEADER_GRADIENT} color="white" py={5}>
              <Text fontSize="xl" fontWeight="700">
                Digital Signature
              </Text>

              <Text fontSize="sm" opacity={0.9} mt={1} fontWeight="normal">
                Customer Signature
              </Text>
            </ModalHeader>

            <ModalCloseButton color="white" mt={2} />

            <ModalBody py={6}>
              <VStack spacing={5} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={textColor} mb={1}>
                    Sign inside the box
                  </Text>

                  <Text fontSize="xs" color="gray.500">
                    Use your mouse, touch screen, or stylus to draw your signature.
                  </Text>
                </Box>

                <Box
                  border="2px dashed"
                  borderColor={PURPLE}
                  borderRadius="14px"
                  bg="white"
                  overflow="hidden"
                  position="relative"
                  width="100%"
                  height="260px"
                >
                  <canvas
                    ref={signatureCanvasRef}
                    width={700}
                    height={260}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      cursor: "crosshair",
                      touchAction: "none",
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={drawSignature}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={drawSignature}
                    onTouchEnd={stopDrawing}
                    onTouchCancel={stopDrawing}
                  />

                  <Text
                    position="absolute"
                    bottom="12px"
                    left="0"
                    right="0"
                    textAlign="center"
                    fontSize="xs"
                    color="gray.400"
                    pointerEvents="none"
                  >
                    Sign here
                  </Text>
                </Box>

                <Button
                  variant="outline"
                  colorScheme="red"
                  borderRadius="12px"
                  onClick={clearSignature}
                >
                  Clear Signature
                </Button>

                <Box
                  p={4}
                  borderRadius="12px"
                  bg={PURPLE_SOFT}
                  border="1px solid"
                  borderColor="#DCD6F3"
                >
                  <Text fontSize="sm" color={PURPLE_DARK}>
                    Your signature will be saved digitally with this service call.
                  </Text>
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter borderTop="1px solid" borderColor={border} gap={3}>
              <Button variant="outline" borderRadius="12px" onClick={closeSignatureDrawer}>
                Cancel
              </Button>

              <Button borderRadius="12px" {...primaryBtn} onClick={saveSignature}>
                Save Signature
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </>
  );
}